import { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { t } from '@lingui/macro'

import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { isStaff } from '@/utils/type-guards'

import { buildSendMssMessageCommand, MssDocumentAttachment } from '../lib/commandBuilder'
import { encodeBase64Utf8, sendEfficienceCommand } from '../lib/helpers'

/** Patient mailbox domain for "Mon espace santé" addresses (matriculeINS@patient.mssante.fr). */
const MES_ADDRESS_DOMAIN = 'patient.mssante.fr'

/** Document format code for PDF — table « MSS Format de document », remote-control spec §4.15. */
const DOCUMENT_FORMAT_PDF = '5'
/** « CR ou fiche de consultation ou de visite » — table « Catégories de documents (typecode) », jeux_de_valeurs. */
const DOCUMENT_CATEGORY_CONSULTATION_REPORT = '11488-4'
/** « Cabinet individuel » — table « Cadre de soin », jeux_de_valeurs. */
const HEALTHCARE_SETTING_PRIVATE_PRACTICE = 'SA07'

type UseSendMssMessageToPatientParams = {
  iframe: HTMLIFrameElement | null
  patientInsNIR?: string
  patientLastname?: string
  patientFirstname?: string
  /** Connected practitioner's MSSanté address, used as the DSN receipt and Reply-To address. */
  senderMssAddress?: string
}

export type SendMssMessageToPatientOptions = {
  /** Optional prefilled subject. */
  title?: string
  /** Optional prefilled body (plain text; encoded to base64 UTF-8). */
  content?: string
  /** End the conversation so the patient cannot reply (X-MSS-MES: FIN). */
  forbidRecipientAnswer?: boolean
  /** Request a DSN delivery receipt (Return-Receipt-To) to the sender's MSSanté address. */
  returnReceipt?: boolean
  /**
   * Mark the message as automatically generated: appends the mandatory mention
   * to the body and sets Reply-To to the sender's MSSanté address (MSS.06).
   */
  automaticNotice?: boolean
  /** PDF documents to attach; Efficience packages them as IHE_XDM ZIP + PDF/A-1 (MSS.28). */
  documents?: Array<{ title: string; contentBase64: string }>
}

/**
 * Drives Efficience's `sendMssMessage` command to open the compose window
 * pre-filled with a message addressed to the current patient's "Mon espace
 * santé" mailbox (built from the qualified INS), and with the connected
 * practitioner's name as the sender label (`senderWording`).
 *
 * Returns `sendMssMessageToPatient` and whether it can run (`canSend`).
 */
export function useSendMssMessageToPatient({
  iframe,
  patientInsNIR,
  patientLastname,
  patientFirstname,
  senderMssAddress,
}: UseSendMssMessageToPatientParams) {
  const authUser = useAppSelector(selectAuthUser)

  // Sender label "Intitulé_BAL" for a personal professional mailbox: NOM Prénom (MSS.08).
  const senderWording = isStaff(authUser) ? [authUser.userInfo.surname, authUser.userInfo.name].filter(Boolean).join(' ') : undefined

  const canSend = Boolean(iframe?.contentWindow && patientInsNIR && patientLastname && patientFirstname)

  const sendMssMessageToPatient = useCallback(
    (options: SendMssMessageToPatientOptions = {}) => {
      if (!iframe?.contentWindow || !patientInsNIR || !patientLastname || !patientFirstname) return

      let content = options.content
      if (options.automaticNotice && senderMssAddress) {
        const notice = t`This message was generated automatically. To reach your practitioner, write to: ${senderMssAddress}`
        content = [content, notice].filter(Boolean).join('\n\n')
      }

      const attachments: MssDocumentAttachment[] | undefined = options.documents?.map((doc) => ({
        fileContentInBase64: doc.contentBase64,
        documentTitle: doc.title,
        documentDescription: doc.title,
        documentCategory: DOCUMENT_CATEGORY_CONSULTATION_REPORT,
        documentFormat: DOCUMENT_FORMAT_PDF,
        healthcareSetting: HEALTHCARE_SETTING_PRIVATE_PRACTICE,
      }))

      const xml = buildSendMssMessageCommand(uuidv4(), {
        recipients: [`${patientInsNIR}@${MES_ADDRESS_DOMAIN}`],
        senderWording,
        title: options.title,
        contentBase64: content ? encodeBase64Utf8(content) : undefined,
        forbidRecipientAnswer: options.forbidRecipientAnswer,
        replyTo: options.automaticNotice ? senderMssAddress : undefined,
        returnReceiptTo: options.returnReceipt ? senderMssAddress : undefined,
        patient: { name: patientLastname, given: patientFirstname, ins: patientInsNIR },
        attachments,
        sendInBackground: false,
      })

      sendEfficienceCommand(iframe, xml)
    },
    [iframe, patientInsNIR, patientLastname, patientFirstname, senderWording, senderMssAddress],
  )

  return { sendMssMessageToPatient, canSend }
}
